import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Zap, Award, Users } from 'lucide-react';
import { mockData } from '../mock';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const backgroundImages = [
    {
      url: '/images/hero-1.jpg',
      alt: 'Professionelle Elektroinstallation'
    },
    {
      url: '/images/hero-2.jpg',
      alt: 'Elektrische Verkabelung'
    },
    {
      url: '/images/hero-3.jpg',
      alt: 'Photovoltaikanlage'
    },
    {
      url: '/images/hero-4.jpg',
      alt: 'Elektrotechniker bei der Arbeit'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages.length);
    }, 5000); // Wechsel alle 5 Sekunden

    return () => clearInterval(timer);
  }, [backgroundImages.length]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1632]/80 via-[#1a2642]/70 to-[#0a1632]/80"></div>
          </div>
        ))}
        
        {/* Slide indicators */}
        <div className="absolute bottom-8 right-8 flex space-x-2 z-20">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-cyan-400 scale-125' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 pt-36 md:pt-40 lg:pt-44">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Heading */}
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-white">{mockData.company.heroSlogan.split(' ').slice(0, 2).join(' ')}</span>
              <br/>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                {mockData.company.heroSlogan.split(' ').slice(2).join(' ')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {mockData.company.heroSubtitle}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Button 
              onClick={() => scrollToSection('#contact')}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300 hover:shadow-cyan-500/25 hover:scale-105"
            >
              <Zap className="mr-2 h-5 w-5" />
              Jetzt anfragen
            </Button>
            <Button 
              onClick={() => scrollToSection('#services')}
              variant="outline"
              size="lg"
              className="border-2 border-white/20 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 hover:border-white/30 px-8 py-4 text-lg font-semibold transition-all duration-300"
            >
              Leistungen entdecken
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="flex flex-col items-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-white font-semibold mb-2">Staatlich geprüft</h3>
              <p className="text-white/70 text-sm text-center">Zertifizierter Elektrotechniker</p>
            </div>
            
            <div className="flex flex-col items-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-white font-semibold mb-2">Modern & Sicher</h3>
              <p className="text-white/70 text-sm text-center">Nach neuesten VDE-Normen</p>
            </div>
            
            <div className="flex flex-col items-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-cyan-400 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-white font-semibold mb-2">Regional</h3>
              <p className="text-white/70 text-sm text-center">Persönlicher Ansprechpartner</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-gradient-to-b from-cyan-400 to-blue-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;